import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '../ui/badge';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, Users, CheckSquare, AlertTriangle, BarChart2, Bell, Cake, ArrowRight } from 'lucide-react';

const WIDGET_ICON_MAP = { TrendingUp, Users, CheckSquare, AlertTriangle, BarChart2, Bell, Cake };

const WidgetCard = ({ widget }) => {
  const navigate = useNavigate();
  const { titulo, tipo_viz, datos, icono, color_acento } = widget;
  const d = datos?.datos;
  const resumen = datos?.resumen_texto || '';
  const herramienta = datos?.metadata?.herramienta;
  const IconComp = WIDGET_ICON_MAP[icono] || BarChart2;
  const esLicitaciones = herramienta === 'licitaciones_abiertas_widget';
  const esCumpleanos = herramienta === 'cumpleanos_proximos';

  return (
    <div className="bg-white rounded-xl border border-ink-100 p-4 shadow-sm" data-testid={`widget-${widget.id}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <IconComp className="w-4 h-4 shrink-0" style={{ color: color_acento || '#1A3C6E' }} />
          <h3 className="text-sm font-semibold text-ink-800 truncate">{titulo}</h3>
        </div>
        {esLicitaciones && Array.isArray(d) && d.length > 0 && (
          <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-[10px]">
            {d.length} aviso{d.length === 1 ? '' : 's'}
          </Badge>
        )}
      </div>
      {resumen && <p className="text-xs text-ink-400 mb-3">{resumen}</p>}

      {tipo_viz === 'kpi' && d !== undefined && d !== null && (
        <div className="text-2xl font-bold" style={{ color: color_acento || '#1A3C6E' }}>
          {typeof d === 'object' && !Array.isArray(d)
            ? (Object.values(d)[0] ?? 0)
            : Array.isArray(d) ? d.length : d}
        </div>
      )}

      {tipo_viz === 'barras' && Array.isArray(d) && d.length > 0 && (
        <ResponsiveContainer width="100%" height={120}>
          <BarChart data={d}>
            <XAxis dataKey="_id" tick={{ fontSize: 10 }} />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip />
            <Bar dataKey="total" fill={color_acento || '#1A3C6E'} radius={[3, 3, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
      )}

      {esLicitaciones && Array.isArray(d) && d.length > 0 && (
        <>
          <ul className="space-y-2" data-testid="widget-licitaciones-list">
            {d.slice(0, 5).map((item) => (
              <li key={item.id} className="text-xs py-2 border-b border-ink-50 last:border-0" data-testid={`widget-licitacion-${item.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-ink-800 line-clamp-2">{item.titulo_corto}</p>
                    <p className="text-[11px] text-ink-500 mt-0.5 truncate">{item.organo}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <Badge className="bg-ink-50 text-ink-600 border-ink-200 text-[9px] uppercase tracking-wide">
                        {item.fuente}
                      </Badge>
                      {item.cpv && <span className="text-[10px] text-ink-400">CPV {item.cpv}</span>}
                      {item.fecha_publicacion && <span className="text-[10px] text-ink-400">{item.fecha_publicacion}</span>}
                    </div>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-[11px] font-semibold text-amber-700">{item.importe_str}</p>
                    {item.url && (
                      <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-[10px] text-brand-600 hover:underline" data-testid={`widget-licitacion-link-${item.id}`}>
                        Ver ficha ↗
                      </a>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
          <div className="mt-2 pt-2 border-t border-ink-100 flex items-center justify-between">
            <span className="text-[10px] text-ink-400">Detectadas por job semanal</span>
            <button onClick={() => navigate('/prospeccion?tab=licitaciones')} className="text-[11px] text-brand-600 font-medium hover:underline inline-flex items-center gap-1" data-testid="widget-licitaciones-ver-todas">
              Ver todas <ArrowRight className="w-3 h-3" />
            </button>
          </div>
        </>
      )}

      {esCumpleanos && Array.isArray(d) && d.length > 0 && (
        <ul className="space-y-1.5" data-testid="widget-cumpleanos-list">
          {d.slice(0, 6).map((item) => (
            <li key={item.id} className="text-xs py-1.5 border-b border-pink-50 last:border-0 flex items-center justify-between gap-2" data-testid={`widget-cumple-${item.id}`}>
              <div className="min-w-0 flex-1">
                <div className="font-medium text-ink-800 truncate flex items-center gap-1.5">
                  <span>🎂</span> {item.nombre}
                </div>
                <div className="text-[11px] text-ink-500">
                  {item.fecha_proxima} · cumple {item.edad_a_cumplir} años
                </div>
              </div>
              <Badge className={`text-[10px] shrink-0 ${
                item.dias_restantes === 0 ? 'bg-pink-600 text-white border-pink-600' :
                item.dias_restantes <= 7 ? 'bg-pink-100 text-pink-700 border-pink-200' :
                'bg-ink-50 text-ink-600 border-ink-200'
              } border`}>
                {item.dias_restantes === 0 ? '¡HOY!' : `en ${item.dias_restantes}d`}
              </Badge>
            </li>
          ))}
        </ul>
      )}

      {!esLicitaciones && !esCumpleanos && (tipo_viz === 'lista' || tipo_viz === 'tabla') && Array.isArray(d) && (
        <ul className="space-y-1">
          {d.slice(0, 5).map((item, i) => (
            <li key={i} className="text-xs text-ink-600 py-0.5 border-b border-ink-50 last:border-0">
              {typeof item === 'object'
                ? Object.values(item).filter(Boolean).slice(0, 2).join(' · ')
                : item}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default WidgetCard;
